import React, { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useAuth } from "../../contexts/AuthContext";
import { useToast } from "../../contexts/ToastContext";
import leaveService from "../../services/leaveService";
import PageHeader from "../../components/ui/PageHeader";
import { 
  FileText, 
  Upload, 
  X, 
  Download, 
  Calendar, 
  Clock, 
  User, 
  FileCheck,
  AlertCircle,
  CheckCircle
} from "lucide-react";
import "../../styles/dashboard.css";

const LeaveAttachmentPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const toast = useToast();

  const [leave, setLeave] = useState(null);
  const [attachments, setAttachments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);

  const [selectedFile, setSelectedFile] = useState(null);
  const [fileTitle, setFileTitle] = useState("");
  const [fileDescription, setFileDescription] = useState("");

  useEffect(() => {
    loadLeaveDetails();
    loadAttachments();
  }, [id]);

  const loadLeaveDetails = async () => {
    try {
      const res = await leaveService.getApprovedHistory({});
      const leaveDetails = res.find(l => l.id === parseInt(id));
      setLeave(leaveDetails);
    } catch (err) {
      console.error("Error loading leave details:", err);
      toast.error("Impossible de charger les détails du congé.");
    }
  };

  const loadAttachments = async () => {
    try {
      const res = await leaveService.getAdditionalAttachments(id);
      setAttachments(res || []);
    } catch (err) {
      console.error("Error loading attachments:", err);
      // Continue even if attachments fail to load
    } finally {
      setLoading(false);
    }
  };

  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (file) {
      // Validate file type
      const allowedTypes = ['application/pdf', 'image/jpeg', 'image/jpg', 'image/png'];
      if (!allowedTypes.includes(file.type)) {
        toast.error("Format de fichier non autorisé. Formats acceptés : PDF, JPG, JPEG, PNG");
        return;
      }

      // Validate file size (max 10MB)
      const maxSize = 10 * 1024 * 1024;
      if (file.size > maxSize) {
        toast.error("La taille du fichier ne doit pas dépasser 10MB");
        return;
      }

      setSelectedFile(file);
      if (!fileTitle) {
        setFileTitle(file.name);
      }
    }
  };

  const handleRemoveFile = () => {
    setSelectedFile(null);
    setFileTitle("");
    setFileDescription("");
  };

  const handleUpload = async (e) => {
    e.preventDefault();
    
    if (!selectedFile) {
      toast.error("Veuillez sélectionner un fichier");
      return;
    }

    if (!fileTitle.trim()) {
      toast.error("Veuillez saisir un titre pour le document");
      return;
    }

    setUploading(true);
    try {
      await leaveService.uploadAdditionalAttachment(id, selectedFile, fileDescription);
      toast.success("Document complémentaire ajouté avec succès");
      
      // Reload attachments
      await loadAttachments();
      
      // Reset form
      setSelectedFile(null);
      setFileTitle("");
      setFileDescription("");
      
      // Redirect back to history after short delay
      setTimeout(() => {
        navigate("/rh/leaves/history");
      }, 1500);
      
    } catch (err) {
      console.error("Error uploading attachment:", err);
      toast.error("Erreur lors de l'ajout du document");
    } finally {
      setUploading(false);
    }
  };

  const handleDownloadAttachment = async (attachment) => {
    try {
      const blob = await leaveService.downloadAttachment(id);
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = attachment.file_name || 'document';
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (err) {
      console.error("Error downloading attachment:", err);
      toast.error("Erreur lors du téléchargement");
    }
  };

  const handleCancel = () => {
    navigate("/rh/leaves/history");
  };

  if (loading) {
    return (
      <div style={{ padding: "24px" }}>
        <div style={{ padding: "40px", textAlign: "center", color: "var(--text-muted)" }}>
          Chargement des informations...
        </div>
      </div>
    );
  }

  if (!leave) {
    return (
      <div style={{ padding: "24px" }}>
        <div style={{ padding: "40px", textAlign: "center", color: "var(--text-muted)" }}>
          Congé non trouvé
        </div>
      </div>
    );
  }

  return (
    <div style={{ padding: "24px" }}>
      <PageHeader
        title="Ajouter un document complémentaire"
        subtitle="Ajoutez une pièce jointe complémentaire à cette demande de congé"
        actions={
          <button
            onClick={handleCancel}
            className="btn btn-outline"
            style={{ display: "flex", alignItems: "center", gap: "8px" }}
          >
            <X size={16} />
            <span>Annuler</span>
          </button>
        }
      />

      {/* Leave Summary Card */}
      <div className="ui-card" style={{ marginBottom: "24px" }}>
        <div style={{ display: "flex", alignItems: "center", gap: "12px", marginBottom: "20px" }}>
          <div style={{
            width: "48px",
            height: "48px",
            borderRadius: "12px",
            background: "var(--primary-light)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center"
          }}>
            <FileCheck size={24} style={{ color: "var(--primary)" }} />
          </div>
          <div>
            <h3 style={{ margin: 0, fontSize: "1.1rem", fontWeight: "600", color: "var(--text-main)" }}>
              Informations du congé
            </h3>
            <p style={{ margin: "4px 0 0 0", fontSize: "0.9rem", color: "var(--text-muted)" }}>
              Vérifiez que vous ajoutez le document au bon congé
            </p>
          </div>
        </div>

        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))", gap: "16px" }}>
          <div>
            <label style={{ display: "block", fontSize: "0.85rem", color: "var(--text-muted)", marginBottom: "4px" }}>
              Employé
            </label>
            <div style={{ display: "flex", alignItems: "center", gap: "8px", fontWeight: "500" }}>
              <User size={16} style={{ color: "var(--text-muted)" }} />
              {leave.employee_name}
            </div>
          </div>

          <div>
            <label style={{ display: "block", fontSize: "0.85rem", color: "var(--text-muted)", marginBottom: "4px" }}>
              Type de congé
            </label>
            <div style={{ fontWeight: "500" }}>
              {leave.leave_type_name}
            </div>
          </div>

          <div>
            <label style={{ display: "block", fontSize: "0.85rem", color: "var(--text-muted)", marginBottom: "4px" }}>
              Date de début
            </label>
            <div style={{ display: "flex", alignItems: "center", gap: "8px", fontWeight: "500" }}>
              <Calendar size={16} style={{ color: "var(--text-muted)" }} />
              {new Date(leave.start_date).toLocaleDateString('fr-FR')}
            </div>
          </div>

          <div>
            <label style={{ display: "block", fontSize: "0.85rem", color: "var(--text-muted)", marginBottom: "4px" }}>
              Date de fin
            </label>
            <div style={{ display: "flex", alignItems: "center", gap: "8px", fontWeight: "500" }}>
              <Calendar size={16} style={{ color: "var(--text-muted)" }} />
              {new Date(leave.end_date).toLocaleDateString('fr-FR')}
            </div>
          </div>

          <div>
            <label style={{ display: "block", fontSize: "0.85rem", color: "var(--text-muted)", marginBottom: "4px" }}>
              Durée
            </label>
            <div style={{ display: "flex", alignItems: "center", gap: "8px", fontWeight: "500" }}>
              <Clock size={16} style={{ color: "var(--text-muted)" }} />
              {leave.duration || leave.days_count} jour(s)
            </div>
          </div>

          <div>
            <label style={{ display: "block", fontSize: "0.85rem", color: "var(--text-muted)", marginBottom: "4px" }}>
              Statut
            </label>
            <div style={{ display: "flex", alignItems: "center", gap: "8px", fontWeight: "500" }}>
              {leave.status === "APPROVED" ? (
                <CheckCircle size={16} style={{ color: "#059669" }} />
              ) : (
                <AlertCircle size={16} style={{ color: "#d97706" }} />
              )}
              {leave.status === "APPROVED" ? "Approuvé" : leave.status}
            </div>
          </div>

          {leave.reason && (
            <div style={{ gridColumn: "1 / -1" }}>
              <label style={{ display: "block", fontSize: "0.85rem", color: "var(--text-muted)", marginBottom: "4px" }}>
                Motif
              </label>
              <div style={{ fontSize: "0.9rem", color: "var(--text-secondary)" }}>
                {leave.reason}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Existing Attachments */}
      {attachments.length > 0 && (
        <div className="ui-card" style={{ marginBottom: "24px" }}>
          <h3 style={{ margin: "0 0 16px 0", fontSize: "1rem", fontWeight: "600", color: "var(--text-main)" }}>
            Pièces jointes existantes
          </h3>
          <div style={{ display: "flex", flexDirection: "column", gap: "12px" }}>
            {attachments.map((attachment, index) => (
              <div
                key={index}
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  padding: "12px 16px",
                  background: "var(--bg-subtle)",
                  borderRadius: "8px",
                  border: "1px solid var(--border)"
                }}
              >
                <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
                  <FileText size={20} style={{ color: "var(--primary)" }} />
                  <div>
                    <div style={{ fontWeight: "500", fontSize: "0.9rem" }}>
                      {attachment.file_name || `Document ${index + 1}`}
                    </div>
                    {attachment.description && (
                      <div style={{ fontSize: "0.85rem", color: "var(--text-muted)" }}>
                        {attachment.description}
                      </div>
                    )}
                    {attachment.created_at && (
                      <div style={{ fontSize: "0.8rem", color: "var(--text-light)" }}>
                        Ajouté le {new Date(attachment.created_at).toLocaleDateString('fr-FR')}
                      </div>
                    )}
                  </div>
                </div>
                <button
                  onClick={() => handleDownloadAttachment(attachment)}
                  className="btn btn-outline"
                  style={{ padding: "8px 16px", fontSize: "0.85rem" }}
                >
                  <Download size={16} />
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Upload Form */}
      <div className="ui-card">
        <h3 style={{ margin: "0 0 20px 0", fontSize: "1rem", fontWeight: "600", color: "var(--text-main)" }}>
          Document complémentaire
        </h3>

        <form onSubmit={handleUpload}>
          {/* File Upload */}
          <div style={{ marginBottom: "20px" }}>
            <label style={{ display: "block", fontSize: "0.9rem", fontWeight: "500", marginBottom: "8px" }}>
              Fichier <span style={{ color: "var(--error)" }}>*</span>
            </label>
            
            {!selectedFile ? (
              <div
                style={{
                  border: "2px dashed var(--border)",
                  borderRadius: "12px",
                  padding: "32px",
                  textAlign: "center",
                  cursor: "pointer",
                  transition: "var(--transition-fast)",
                  background: "var(--bg-subtle)"
                }}
                onClick={() => document.getElementById('file-input').click()}
                onMouseEnter={(e) => {
                  e.currentTarget.style.borderColor = "var(--primary)";
                  e.currentTarget.style.background = "var(--primary-light)";
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.borderColor = "var(--border)";
                  e.currentTarget.style.background = "var(--bg-subtle)";
                }}
              >
                <Upload size={32} style={{ color: "var(--primary)", marginBottom: "12px" }} />
                <div style={{ fontSize: "0.95rem", color: "var(--text-main)", marginBottom: "4px" }}>
                  Cliquez pour sélectionner un fichier
                </div>
                <div style={{ fontSize: "0.85rem", color: "var(--text-muted)" }}>
                  Formats acceptés : PDF, JPG, JPEG, PNG (max 10MB)
                </div>
                <input
                  id="file-input"
                  type="file"
                  accept=".pdf,.jpg,.jpeg,.png"
                  onChange={handleFileSelect}
                  style={{ display: "none" }}
                />
              </div>
            ) : (
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  padding: "16px",
                  background: "var(--primary-light)",
                  borderRadius: "8px",
                  border: "1px solid var(--primary)"
                }}
              >
                <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
                  <FileText size={20} style={{ color: "var(--primary)" }} />
                  <div>
                    <div style={{ fontWeight: "500", fontSize: "0.9rem" }}>
                      {selectedFile.name}
                    </div>
                    <div style={{ fontSize: "0.85rem", color: "var(--text-muted)" }}>
                      {(selectedFile.size / 1024 / 1024).toFixed(2)} MB
                    </div>
                  </div>
                </div>
                <button
                  type="button"
                  onClick={handleRemoveFile}
                  style={{
                    padding: "8px",
                    background: "var(--error)",
                    color: "white",
                    border: "none",
                    borderRadius: "6px",
                    cursor: "pointer"
                  }}
                >
                  <X size={16} />
                </button>
              </div>
            )}
          </div>

          {/* File Title */}
          <div style={{ marginBottom: "20px" }}>
            <label style={{ display: "block", fontSize: "0.9rem", fontWeight: "500", marginBottom: "8px" }}>
              Nom / titre du document
            </label>
            <input
              type="text"
              value={fileTitle}
              onChange={(e) => setFileTitle(e.target.value)}
              placeholder="Ex: Certificat médical"
              style={{
                width: "100%",
                padding: "12px 16px",
                border: "1px solid var(--border)",
                borderRadius: "8px",
                fontSize: "0.9rem",
                background: "var(--bg-main)",
                color: "var(--text-main)"
              }}
            />
          </div>

          {/* File Description */}
          <div style={{ marginBottom: "24px" }}>
            <label style={{ display: "block", fontSize: "0.9rem", fontWeight: "500", marginBottom: "8px" }}>
              Description (facultative)
            </label>
            <textarea
              value={fileDescription}
              onChange={(e) => setFileDescription(e.target.value)}
              placeholder="Description du document..."
              rows={3}
              style={{
                width: "100%",
                padding: "12px 16px",
                border: "1px solid var(--border)",
                borderRadius: "8px",
                fontSize: "0.9rem",
                background: "var(--bg-main)",
                color: "var(--text-main)",
                resize: "vertical"
              }}
            />
          </div>

          {/* Info Banner */}
          <div style={{
            display: "flex",
            alignItems: "flex-start",
            gap: "12px",
            padding: "12px 16px",
            background: "var(--info-bg)",
            borderRadius: "8px",
            marginBottom: "24px",
            border: "1px solid var(--info-border)"
          }}>
            <AlertCircle size={20} style={{ color: "var(--info-text)", flexShrink: 0 }} />
            <div style={{ fontSize: "0.85rem", color: "var(--info-text)" }}>
              Ce document sera ajouté aux pièces jointes existantes sans les remplacer. L'historique des documents sera conservé.
            </div>
          </div>

          {/* Action Buttons */}
          <div style={{ display: "flex", gap: "12px", justifyContent: "flex-end" }}>
            <button
              type="button"
              onClick={handleCancel}
              className="btn btn-outline"
              disabled={uploading}
              style={{ padding: "12px 24px" }}
            >
              Annuler
            </button>
            <button
              type="submit"
              className="btn btn-primary"
              disabled={uploading || !selectedFile}
              style={{ 
                padding: "12px 24px",
                display: "flex",
                alignItems: "center",
                gap: "8px"
              }}
            >
              {uploading ? (
                <>
                  <Clock size={16} className="spinner" />
                  <span>Envoi en cours...</span>
                </>
              ) : (
                <>
                  <Upload size={16} />
                  <span>Ajouter le document</span>
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default LeaveAttachmentPage;
